import { ref } from 'vue'
import { toast } from 'vue3-toastify'

import AuthApi from '@/api/AuthApi'
import { i18n } from '@/boot/plugins/i18n'
import { rememberRoute, router } from '@/boot/plugins/router'
import { useAuthStore } from '@/stores/auth'
import { LOGOUT_BEACON_KEY, emitLogoutBeacon } from '@/utils/logout-beacon'
import { beginLogout, endLogout, isLoggingOut } from '@/utils/logout-signal'

export type LogoutReason = 'user' | 'session_expired' | 'cross_tab' | 'idle' | 'email_changed'

export interface LogoutOptions {
  reason?: LogoutReason
  skipServer?: boolean
  scope?: 'all' | 'device'
  navigate?: boolean
}

// A fixed id collapses the burst a single expiry can produce into one toast.
const SESSION_TOAST_ID = 'session-expired'

const REASON_MESSAGES: Record<Exclude<LogoutReason, 'user'>, string> = {
  session_expired: 'Your session has expired. Please sign in again.',
  cross_tab: 'You have been signed out in another tab.',
  idle: 'You were signed out because you were inactive.',
  email_changed: 'Your email address has changed. Sign in with your new address.',
}

const showDialog = ref(false)

/**
 * The single way out of an authenticated session. Aborts in-flight requests,
 * tells the server, clears local state, and redirects — in that order, so no
 * response can land against a half-torn-down session.
 */
export async function performLogout(options: LogoutOptions = {}): Promise<void> {
  // Several 401s can land at once when a token dies mid-page. Only the first
  // gets to run the teardown.
  if (isLoggingOut()) return

  const { reason = 'user', skipServer = false, scope = 'all', navigate = true } = options

  beginLogout()

  try {
    if (!skipServer) {
      // Best effort: a failed revoke must not strand the user in a signed-in UI. An idle browser
      // gives up only its own token; the person may be working on another device.
      const revoke =
        scope === 'device' || reason === 'idle' ? AuthApi.logoutCurrentDevice() : AuthApi.logout()
      await revoke.catch(() => undefined)
    }
  } finally {
    useAuthStore().clearSession()

    if (reason !== 'user') {
      // The redirect below changes the URL, which clears toasts by default and took this one down
      // 0.6s after it appeared. An idle sign-out stays up: the person it explains was away.
      toast.warning(i18n.global.t(REASON_MESSAGES[reason]), {
        toastId: SESSION_TOAST_ID,
        clearOnUrlChange: false,
        ...(reason === 'idle' ? { autoClose: false } : {}),
      })
    }

    showDialog.value = false

    if (navigate) {
      if (reason !== 'user') {
        rememberRoute(`${location.pathname}${location.search}`)
      }

      await router.push('/auth/login').catch(() => undefined)
    }

    // After local teardown, and never on the receive path — a received beacon
    // that re-emits loops straight back to the tab that sent it.
    if (reason !== 'cross_tab') emitLogoutBeacon()

    // Released once the redirect has settled, rather than on a timer: a timer is
    // a race with a delay in front of it.
    endLogout()
  }
}

export const handleSessionExpired = (): Promise<void> =>
  performLogout({ reason: 'session_expired', navigate: location.pathname !== '/auth/connect' })

let crossTabRegistered = false

export function registerCrossTabLogout(): void {
  if (crossTabRegistered) return
  crossTabRegistered = true

  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key !== LOGOUT_BEACON_KEY) return
    if (isLoggingOut()) return
    if (!useAuthStore().isLoggedIn) return

    // The emitting tab already revoked the token server-side, so calling the
    // endpoint here would be a guaranteed 401 — and could loop.
    void performLogout({ reason: 'cross_tab', skipServer: true })
  })
}

export function useLogout() {
  return {
    showDialog,
    trigger: (): void => {
      showDialog.value = true
    },
    cancel: (): void => {
      showDialog.value = false
    },
    confirm: (): Promise<void> => performLogout({ reason: 'user' }),
    performLogout,
  }
}
